import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { resolveSceneById } from './scene-addressing';
import { Tool, type ToolConstructorArgs } from './Tool';

import type { EnhancedToolResult, ToolExecutionContext } from 'js/src/agent/types';
import {
    captureVideoState,
    computeVideoDiff,
    formatVideoDiffAsGitPatch,
    type VideoStateManager,
    type VideoStateSnapshot,
} from 'js/src/agent/video-state';
import { yieldToBrowser } from 'js/src/agent/yieldToBrowser';
import type { SceneFillKind } from 'js/src/data/agent-activity';
import {
    markScenesBusy,
    markSceneTouchedByAgent,
    recordSceneFill,
    startSceneBuilding,
} from 'js/src/data/agent-activity';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

/**
 * Base class for tools that modify video state.
 * Automatically captures before/after snapshots and appends video diffs to tool results.
 * Accesses RootAgent through the execution context for state tracking.
 */
export abstract class VideoStateTool<
    // Either a Zod schema (schema-first tools) or a hand-written args interface
    // (legacy tools); threaded straight through to Tool, which resolves the
    // validated `execute` arg type from it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TSchema = Record<string, any>,
    TConstructorArgs extends ToolConstructorArgs = ToolConstructorArgs,
> extends Tool<TSchema, TConstructorArgs> {
    public override async executeWithValidation(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Record<string, any>,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        // In the shared funnel so every scene-editing tool gets this without opting in.
        // Existing scenes only; a new scene is marked by startSceneBuilding instead.
        const editedSceneId = this.resolveTargetSceneId(args);
        if (editedSceneId != null) {
            this.dispatch(markSceneTouchedByAgent({ sceneIds: [editedSceneId] }));
            // set_text / set_icon never await, so without this the mark and the edit
            // land in one task and React never paints the indicator first.
            await yieldToBrowser();
        }
        return this.executeWithDiff(args, context);
    }

    private async executeWithDiff(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Record<string, any>,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const videoStateManager = context?.videoStateManager;
        if (!videoStateManager) {
            return super.executeWithValidation(args, context);
        }

        const previousSnapshot = await this.captureBeforeState(videoStateManager);
        const result = await super.executeWithValidation(args, context);

        if (result.isSuccess && previousSnapshot) {
            await this.generateDiffResult(result, previousSnapshot, videoStateManager, args);
        }

        return result;
    }

    /**
     * The scene this call edits, or null when it addresses none — a creation or
     * whole-video tool, or an id that doesn't resolve (those report their own errors;
     * marking a scene we can't identify would strand the indicator on the wrong one).
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private resolveTargetSceneId(args: Record<string, any>): string | null {
        if (typeof args.scene_id !== 'string') {
            return null;
        }
        const resolved = resolveSceneById(this.luminary, this.getState(), args.scene_id);
        return 'error' in resolved ? null : resolved.scene.id;
    }

    /**
     * Mark a freshly-created scene (by id) as "building" so the build preview
     * blurs it until its content lands. Inspects the scene's design to decide what
     * it must receive first — an editable, still-blank text asset means it waits
     * for text; an editable media asset means it waits for media; a scene with
     * neither clears right away. Used by the tools that create scenes (add_scene,
     * add_voiceover_scene).
     */
    protected startSceneBuilding(sceneId: string): void {
        const scene = this.luminary.scenes.find((s) => s.id === sceneId);
        if (!scene) {
            return;
        }
        const { needsText, needsMedia, needsIcon } = VideoStateTool.getSceneFillRequirements(scene);
        this.dispatch(startSceneBuilding({ sceneId, needsText, needsMedia, needsIcon }));
    }

    /**
     * Blur the base scene(s) of a freshly-created voicedriven scene group (talking
     * head / voiceover). Unlike text-on-media scenes, a voicedriven base scene carries
     * the voice asset rather than editable text/media, so no set_text/set_media fill
     * ever arrives to clear it — it can't be tracked as "building" and is instead
     * marked blurred for the rest of the turn. Overlay scenes added later go through
     * the normal fill-based startSceneBuilding instead. Used by the voicedriven
     * scene-group tools (add_voiceover_scene_group, add_recorded_voiceover_scene_group,
     * add_talking_head_scene_group).
     */
    protected startBaseSceneBuilding(sceneGroupId: string): void {
        const sceneGroup = this.luminary.sceneGroups.find((group) => group.id === sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const state = this.getState();
        const baseSceneIds = sceneGroup.scenes
            .filter((scene) => !isSceneAnOverlay(state, scene))
            .map((scene) => scene.id);
        markScenesBusy(this.dispatch, baseSceneIds);
    }

    /**
     * Blur a freshly-created motion graphics scene, so it doesn't read as finished-and-empty while
     * the design subagent is still writing its graphic. Marked the same way the design session
     * itself marks it each time it runs.
     */
    protected startMotionGraphicsSceneBuilding(sceneId: string): void {
        markScenesBusy(this.dispatch, [sceneId]);
    }

    /**
     * Snapshot the current scene ids, so a subsequent
     * {@link blurScenesCreatedSince} can tell which scenes an operation created.
     */
    protected snapshotSceneIds(): Set<string> {
        return new Set(this.luminary.scenes.map((scene) => scene.id));
    }

    /**
     * Blur every scene that appeared since the snapshot but that the caller isn't
     * tracking itself. Creating an overlay splits the base scene it lands on, so the
     * remainder of the scene group becomes a *brand-new* base scene alongside the
     * requested overlay. That base scene carries the voice asset rather than editable
     * text/media, so no fill ever arrives to clear it and it can't be tracked as
     * "building" — without this it renders sharp and empty next to its blurred
     * siblings while the rest of the turn is still working.
     */
    protected blurScenesCreatedSince(
        sceneIdsBefore: Set<string>,
        alreadyTrackedSceneIds: string[] = [],
    ): void {
        const tracked = new Set(alreadyTrackedSceneIds);
        const newSceneIds = this.luminary.scenes
            .map((scene) => scene.id)
            .filter((sceneId) => !sceneIdsBefore.has(sceneId) && !tracked.has(sceneId));
        markScenesBusy(this.dispatch, newSceneIds);
    }

    /**
     * Record that content has landed on a building scene (by index) so the build
     * preview can clear its blur. A scene unblurs once it has the text and/or media
     * it needs; a 'review' clears it on its own. No-op for scenes that aren't
     * currently building.
     */
    protected recordSceneFill(sceneIndex: number, kind: SceneFillKind): void {
        const sceneId = this.luminary.scenes[sceneIndex]?.id;
        if (sceneId != null) {
            this.dispatch(recordSceneFill({ sceneId, kind }));
        }
    }

    /** recordSceneFill for several scenes at once (e.g. image_ask reviewing a batch). */
    protected recordSceneFillForScenes(sceneIndexes: number[], kind: SceneFillKind): void {
        sceneIndexes.forEach((sceneIndex) => this.recordSceneFill(sceneIndex, kind));
    }

    /**
     * What content the build preview should wait for before clearing a scene's
     * blur: an editable text asset that already carries content means real text is
     * being written and we wait for it (an editable-but-empty slot isn't
     * guaranteed to get filled, so it doesn't hold the blur); an editable media
     * asset means the visual still has to be set; an editable icon asset means an
     * icon still has to be chosen. A scene with nothing to wait on unblurs right
     * away.
     */
    public static getSceneFillRequirements(scene: Scene): {
        needsText: boolean;
        needsMedia: boolean;
        needsIcon: boolean;
    } {
        const needsText = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .some(
                (asset) =>
                    asset.isEditable && VideoData.isTextAsset(asset) && asset.text.trim() !== '',
            );
        const needsMedia = scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
            .some((asset) => asset.isEditable);
        const needsIcon = scene
            .getAssetsByType(VideoData.ICON_ASSET_TYPE)
            .some((asset) => asset.isEditable);
        return { needsText, needsMedia, needsIcon };
    }

    private async captureBeforeState(
        videoStateManager: VideoStateManager,
    ): Promise<VideoStateSnapshot | null> {
        let previousSnapshot = videoStateManager.getLastSnapshot();

        if (!previousSnapshot) {
            previousSnapshot = await captureVideoState(
                this.luminary,
                undefined,
                videoStateManager.getStateAccessor(),
                videoStateManager.getIncludeColorNames(),
            );
            videoStateManager.setSnapshot(previousSnapshot);
        }

        return previousSnapshot;
    }

    /**
     * Generate diff and append to result
     */
    private async generateDiffResult(
        result: EnhancedToolResult,
        previousSnapshot: VideoStateSnapshot,
        videoStateManager: VideoStateManager,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Record<string, any>,
    ): Promise<void> {
        try {
            const currentSnapshot = await captureVideoState(
                this.luminary,
                undefined,
                videoStateManager.getStateAccessor(),
                videoStateManager.getIncludeColorNames(),
            );
            const diff = computeVideoDiff(previousSnapshot, currentSnapshot);
            const sceneIndexFilter = this.extractSceneIndexFilter(args);
            const diffText = formatVideoDiffAsGitPatch(
                diff,
                sceneIndexFilter,
                videoStateManager.getSceneAddressing(),
            );
            if (diffText) {
                result.result = `${result.result}\n\n${diffText}`;
            } else {
                // The tool reported success but the video state is unchanged (e.g.
                // the requested edit was already in effect, or a built-in limit
                // prevented it). Say so explicitly so the model doesn't infer the
                // edit took effect from the absence of a diff.
                result.result = `${result.result}\n\n_(No changes were made to the video.)_`;
            }
            videoStateManager.setSnapshot(currentSnapshot);
        } catch (error) {
            console.error('Failed to generate diff for tool result:', error);
        }
    }

    /**
     * Resolve the scene index the appended diff should be scoped to. Legacy index-addressed forks
     * still send `scene_index` directly. The scene_id tools (AP-931) send `scene_id` only, so resolve
     * it back to an index against the post-edit state — these edits don't renumber scenes, and for a
     * removal (delete_scene) the id no longer resolves, leaving the diff unfiltered, which is correct
     * for a removal diff.
     */
    private extractSceneIndexFilter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Record<string, any>,
    ): number | undefined {
        if (typeof args.scene_index === 'number') {
            return args.scene_index;
        }
        if (typeof args.scene_id === 'string') {
            const resolved = resolveSceneById(this.luminary, this.getState(), args.scene_id);
            return 'error' in resolved ? undefined : resolved.sceneIndex;
        }
        return undefined;
    }
}
